import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
    useContext,
} from 'react'
import {
    Country,
    State,
    City,
} from 'country-state-city'

import ExpandableComponentContext from '../../Contexts/ExpandableComponentContext'

const countriesWithoutStates = [
    'United Kingdom',
    'Netherlands',
    'Belgium',
    'Denmark',
    'Sweden',
    'Norway',
    'Finland',
    'Ireland',
]

const countries = Country.getAllCountries()
const usCountry = countries.find(c => c.isoCode === 'US')

const LocationSelector = forwardRef(({ data, readonly }, ref) => {

    const ExpandableComponentData = useContext(ExpandableComponentContext)

    const isInteracted = ExpandableComponentData ? ExpandableComponentData?.isInteracted : false

    const [states, setStates] = useState([])
    const [cities, setCities] = useState([])

    const [selectedCountry, setSelectedCountry] = useState(data?.country)
    const [selectedState, setSelectedState] = useState(data?.state)
    const [selectedCity, setSelectedCity] = useState(data?.city)
    const [zipCode, setZipCode] = useState(data?.zipCode ?? "")
    const [error, setError] = useState(false)

    const isStateRequired = selectedCountry
        ? !countriesWithoutStates.includes(selectedCountry.name)
        : true

    useEffect(() => {

        let defaultCountry = usCountry

        if (data?.country) {
            const preselected = countries.find(c => c.name === data?.country)
            if (preselected) {
                defaultCountry = preselected
            }
        }

        setSelectedCountry(defaultCountry)
    }, [data?.country])

    useEffect(() => {
        if (!selectedCountry) {
            setStates([])
            setSelectedState(null)
            setCities([])
            setSelectedCity(null)
            return
        }

        const countryStates = State.getStatesOfCountry(selectedCountry.isoCode)
        setStates(countryStates)

        if (!isStateRequired || countryStates.length === 0) {
            setSelectedState(null)
        } else if (data?.state) {
            // Stored state may be the full name ("California") or the ISO code ("CA"), and can
            // differ in casing, so match on both rather than only an exact name — otherwise the
            // dropdown stays empty and (since cities derive from the state) the city blanks too.
            const target = String(data.state).trim().toLowerCase()
            const matchedState = countryStates.find(
                s => s.name.toLowerCase() === target || s.isoCode.toLowerCase() === target,
            )
            setSelectedState(matchedState || null)
        } else {
            setSelectedState(null)
        }

        setCities([])
        setSelectedCity(null)
    }, [selectedCountry, data?.state, isStateRequired])

    useEffect(() => {
        if (!selectedCountry) {
            setCities([])
            setSelectedCity(null)
            return
        }

        let cityList = []

        if (!isStateRequired) {
            cityList = City.getCitiesOfCountry(selectedCountry.isoCode)
        } else if (selectedState) {
            cityList = City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode)
        }

        setCities(cityList)

        if (data?.city) {
            const target = String(data.city).trim().toLowerCase()
            const matchedCity = cityList.find(c => c.name.toLowerCase() === target)
            setSelectedCity(matchedCity || null)
        } else {
            setSelectedCity(null)
        }
    }, [selectedState, selectedCountry, data?.city, isStateRequired])

    useEffect(() => {
        setZipCode(data.zipCode || '')
    }, [data?.zipCode])

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            const finalZip = zipCode.trim()

            let isValid =
                selectedCountry &&
                (!isStateRequired || selectedState) &&
                finalZip !== ''

            if (cities.length > 0) {
                isValid = isValid && selectedCity
            }

            if (isValid) {
                setError(false)
            }
            else {
                if (isInteracted) {
                    setError(true)
                }
            }

            return {
                isValid,
                value: {
                    country: selectedCountry?.name,
                    state: selectedState?.name || '',
                    city: selectedCity?.name,
                    zipCode: finalZip
                }
            }
        }
    }))

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Location
            </label>

            <div className="grid grid-cols-2 gap-4">
                {readonly ? (
                    <input
                        type="text"
                        readOnly
                        className="border border-gray-300 rounded-md p-2 bg-gray-100 w-full"
                        value={selectedCountry?.name || ''}
                    />
                ) : (
                    <select
                        className="border border-gray-300 rounded-md p-2 bg-white w-full"
                        value={selectedCountry?.isoCode || ''}
                        onChange={e => {
                            const selected = countries.find(c => c.isoCode === e.target.value)
                            setSelectedCountry(selected)
                            setSelectedState(null)
                            setSelectedCity(null)
                            setCities([])
                            setZipCode('')
                            setError(false)
                        }}
                    >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                            <option key={country.isoCode} value={country.isoCode}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                )}

                {readonly ? (
                    <input
                        type="text"
                        readOnly
                        className="border border-gray-300 rounded-md p-2 bg-gray-100 w-full"
                        value={selectedState?.name || ''}
                    />
                ) : (
                    <select
                        className="border border-gray-300 rounded-md p-2 bg-white w-full"
                        value={selectedState?.isoCode || ''}
                        onChange={e => {
                            const selected = states.find(s => s.isoCode === e.target.value)
                            setSelectedState(selected)
                            setSelectedCity(null)
                            setCities([])
                            setZipCode('')
                            setError(false)
                        }}
                        disabled={!isStateRequired || states.length === 0}
                    >
                        <option value="">Select State</option>
                        {states.map(state => (
                            <option key={state.isoCode} value={state.isoCode}>
                                {state.name}
                            </option>
                        ))}
                    </select>
                )}

                {readonly ? (
                    <input
                        type="text"
                        readOnly
                        className="border border-gray-300 rounded-md p-2 bg-gray-100 w-full"
                        value={selectedCity?.name || ''}
                    />
                ) : (
                    <select
                        className="border border-gray-300 rounded-md p-2 bg-white w-full"
                        value={selectedCity?.name || ''}
                        onChange={e => {
                            const selected = cities.find(c => c.name === e.target.value)
                            setSelectedCity(selected)
                            setZipCode('')
                            setError(false)
                        }}
                        disabled={cities.length === 0}
                    >
                        <option value="">Select City</option>
                        {cities.map(city => (
                            <option key={city.name} value={city.name}>
                                {city.name}
                            </option>
                        ))}
                    </select>
                )}

                {readonly ? (
                    <input
                        type="text"
                        readOnly
                        className="border border-gray-300 rounded-md p-2 bg-gray-100 w-full"
                        value={zipCode}
                    />
                ) : (
                    <input
                        type="text"
                        className="border border-gray-300 rounded-md p-2 bg-white w-full"
                        placeholder='Enter Zip Code, fill "00000" if NA'
                        value={zipCode}
                        onChange={e => {
                            setZipCode(e.target.value)
                            setError(false)
                        }}
                    />
                )}
            </div>

            {error && (
                <p className="text-sm text-red-600 mt-2">Fill location fields correctly</p>
            )}
        </div>
    )
})

export default LocationSelector
